<template>
  <div class="gap-4 pb-4 flex flex-1 flex-col items-center justify-between">
    <div />

    <div
      class="gap-8 py-8 flex flex-col items-center justify-center text-center"
    >
      <span class="text-[36px]">{{ score.text }}</span>
      <BaseStars :value="score.stars" :is-vertical="false" :size="70" />
      <span class="text-xl">{{ score.value }}/{{ exercises.list.length }}</span>
    </div>

    <!-- <div
      v-if="isDemo"
      class="heartbeat gap-4 px-8 mb-8 flex flex-col items-center text-center text-base"
      style="animation-delay: 2s"
    >
      <span>{{ $t('hub.exercises.results.getAccessMessage') }}</span>
    </div> -->

    <div class="w-full px-4 gap-4 flex flex-col items-center">
      <!-- <BaseButton
        v-if="!isAuthorized"
        color="primary-gradient"
        @click="handleSaveResults"
      >
        {{ $t('hub.exercises.results.register') }}
      </BaseButton> -->

      <BaseButton
        v-if="isDemo"
        color="primary-gradient"
        @click="() => $emit('close-demo')"
      >
        Close demo
      </BaseButton>

      <BaseButton :color="isDemo ? 'light' : 'dark'" @click="handleTryAgain">
        <span v-if="isDemo">{{ $t('hub.exercises.results.repeatDemo') }}</span>
        <span v-else>{{ $t('hub.exercises.results.repeatTest') }}</span>
      </BaseButton>

      <BaseButton v-if="!isDemo" color="light" :to="{ name: 'hub.sections' }">
        {{ $t('hub.exercises.results.backToTopics') }}
      </BaseButton>

      <BaseButton
        v-if="!isHistoryVisible"
        color="transparent"
        @click="handleShowHistory"
      >
        {{ $t('hub.exercises.results.answerHistory') }}
      </BaseButton>
    </div>

    <template v-if="isHistoryVisible">
      <ExercisesResultsHistory class="w-full" />

      <div class="w-full px-4 gap-4 flex flex-col items-center">
        <BaseButton
          v-if="isDemo"
          color="primary-gradient"
          @click="() => $emit('close-demo')"
        >
          Close demo
        </BaseButton>

        <BaseButton :color="isDemo ? 'light' : 'dark'" @click="handleTryAgain">
          <span v-if="isDemo">{{
            $t('hub.exercises.results.repeatDemo')
          }}</span>
          <span v-else>{{ $t('hub.exercises.results.repeatTest') }}</span>
        </BaseButton>

        <BaseButton v-if="false" color="light" :to="{ name: 'hub.sections' }">
          {{ $t('hub.exercises.results.backToTopics') }}
        </BaseButton>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'

import { ADAPTATION_ALLOWED_TOPICS } from '@/utils/consts'
import { defineStarsByScore } from '@/utils/base'

import BaseStars from '@/components/Base/BaseStars.vue'
import BaseButton from '@/components/Base/BaseButton.vue'

import ExercisesResultsHistory from '@/components/Hub/Exercises/ExercisesResultsHistory.vue'

export default {
  name: 'HubExercisesResults',

  components: { BaseStars, BaseButton, ExercisesResultsHistory },

  props: {
    sectionSlug: { type: String, required: true },
    topicSlug: { type: String, required: true },

    isDemo: { type: Boolean, default: false }
  },

  data: () => ({
    isHistoryVisible: false
  }),

  computed: {
    ...mapState('user', ['user']),
    ...mapState('exercises', ['exercises']),
    ...mapGetters('exercises', ['totalCorrectAnswers']),
    // ...mapGetters('auth', ['isAuthorized']),

    score() {
      const value = this.totalCorrectAnswers
      const max = this.exercises.list.length

      return {
        value,
        stars: defineStarsByScore(value, max),
        text: this.defineTextByScore(value)
      }
    },

    sectionRecords() {
      const isAdaptationAllowed = ADAPTATION_ALLOWED_TOPICS.includes(
        this.topicSlug
      )

      const defineSmartStats = () => {
        return this.exercises.history.reduce((result, item) => {
          const answerCategory = item.exercise.answers[0].toLowerCase()

          if (!result[answerCategory]) {
            result[answerCategory] = { correct: 0, incorrect: 0 }
          }

          if (item.isCorrect) {
            result[answerCategory].correct += 1
          } else {
            result[answerCategory].incorrect += 1
          }

          return result
        }, {})
      }

      const smart = isAdaptationAllowed ? defineSmartStats() : {}
      const basic = { stars: this.score.stars }
      const sectionRecord = { smart, ...basic }

      return {
        sectionSlug: this.sectionSlug,
        topicSlug: this.topicSlug,
        sectionRecord
      }
    }
  },

  created() {
    this.$gtag.event('exercise_finished', {
      stars: this.score.stars
    })

    if (!this.user) return

    this.updateSectionRecords(this.sectionRecords)
  },

  mounted() {
    const defineParticleCount = () => {
      if (this.score.stars === 0) return 5
      if (this.score.stars === 1) return 10
      if (this.score.stars === 2) return 50
      if (this.score.stars === 3) return 200
    }

    this.setUiConfetti({
      isVisible: true,
      left: '50%',
      top: '0%',
      particleCount: defineParticleCount()
    })
  },

  beforeDestroy() {
    this.setUiConfetti({ isVisible: false })
  },

  methods: {
    ...mapActions('user', {
      updateSectionRecords: 'UPDATE_SECTION_RECORDS'
    }),
    ...mapMutations('ui', {
      setUiConfetti: 'SET_UI_CONFETTI',
      setUiPaywallModalIsVisible: 'SET_UI_PAYWALL_MODAL_IS_VISIBLE'
    }),

    handleShowHistory() {
      this.isHistoryVisible = true
    },

    handleTryAgain() {
      this.$emit('try-again')
    },

    defineTextByScore(score) {
      const max = this.exercises.list.length
      const scorePercentage = (score / max) * 100

      if (scorePercentage === 0)
        return this.$t('hub.exercises.results.score.tryAgain')
      if (scorePercentage < 49)
        return this.$t('hub.exercises.results.score.notBad')
      if (scorePercentage < 100)
        return this.$t('hub.exercises.results.score.good')

      return this.$t('hub.exercises.results.score.excellent')
    }

    // handleSaveResults() {
    //   this.$gtag.event('exercise_save_results_clicked')

    //   this.$router.push({
    //     query: {
    //       action: 'open-registration',
    //       sectionRecords: JSON.stringify(this.sectionRecords)
    //     }
    //   })
    // }
  }
}
</script>

<style scoped lang="scss">
@keyframes heartbeat {
  0%,
  10% {
    transform: scale(1);
  }
  15% {
    transform: scale(0.95);
  }
  20% {
    transform: scale(1);
  }
  25% {
    transform: scale(0.95);
  }
  30% {
    transform: scale(1);
  }
  100% {
    transform: scale(1); /* Pause for the remaining time */
  }
}

.heartbeat {
  animation: heartbeat 10s ease-in-out infinite; /* 10s total duration */
}
</style>
